import {AxiosError, AxiosResponse} from 'axios'
import {useFormContext} from 'react-hook-form'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {PatientExtended} from '@/features/patients/types'
import {remapPatientToDefaultValues} from '@/features/patients/utils'
import {usePatientDetailsStore} from '@/features/patients/store/usePatientDetailsStore'
import {FormErrorResponse} from '@/types'
import {useScrollContext} from '@/hooks/useScrollContext'
import {z, ZodSchema} from 'zod'

export const usePatientFormSubmission = ({formSchema}: {formSchema: ZodSchema}) => {
    const {t} = useTranslation()
    const form = useFormContext<z.infer<typeof formSchema>>()
    const setEditingFieldsGroup = usePatientDetailsStore(state => state.setEditingFieldsGroup)
    const setIsErrorBannerVisible = usePatientDetailsStore(state => state.setIsErrorBannerVisible)
    const {scrollToTop} = useScrollContext()

    const onSuccessSubmit = (response: AxiosResponse<PatientExtended>) => {
        toast.success(t('commons:update_success'))

        // Reset the form
        const remappedResponse = remapPatientToDefaultValues(response.data)
        form.reset(remappedResponse)

        // Update the query cache
        queryClient.setQueryData([QUERY_KEYS.PATIENTS, response.data.id], response.data)

        setEditingFieldsGroup(null)
        setIsErrorBannerVisible(false)

        void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS]})
    }

    const onErrorSubmit = (errors: AxiosError<{errors: FormErrorResponse[]}>) => {
        setIsErrorBannerVisible(true)
        if (errors?.response?.data.errors) {
            errors.response.data.errors.forEach(error => {
                if (error.field == 'taxCode' && error.rule == 'regex') {
                    form.setError('taxCode', {
                        message: t('errors:invalid_tax_code'),
                        type: 'custom'
                    })
                }

                if (error.field == 'taxCode' && error.rule == 'database.exists') {
                    form.setError('taxCode', {
                        message: t('errors:tax_code_already_exists'),
                        type: 'custom'
                    })
                }
            })
        }
        console.error(errors)
        scrollToTop()
    }

    return {
        onSuccessSubmit,
        onErrorSubmit,
        scrollToTop
    }
}
